import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, of, TimeoutError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../environments/environmentCAS';

export const CAS_COMMISSION_STORAGE_KEY = 'cas_commission_settings_v1';

export interface CasCommissionLocalState {
  /** Taux / code appliqué si aucune valeur spécifique n’existe pour un partenaire */
  defaultCode: string;
  partners: Record<string, string>;
}

/**
 * Commissions pour le comparateur santé (écran CAS) : persistance côté API Brokins (FastAPI CAC),
 * même URL que les utilisateurs connectés (`/api/comparaison/commission_settings`).
 * Ne pas confondre avec l’admin Spring (`8082` / `v1/admin/commission-settings`) qui exige un autre JWT.
 */
@Injectable({ providedIn: 'root' })
export class CommissionSettingsService {
  private readonly baseUrl = `${environment.apiUrl}/api/comparaison/commission_settings`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  getLocalState(): CasCommissionLocalState {
    try {
      const raw = localStorage.getItem(CAS_COMMISSION_STORAGE_KEY);
      if (!raw) {
        return { defaultCode: '', partners: {} };
      }
      const parsed = JSON.parse(raw) as CasCommissionLocalState;
      return {
        defaultCode: (parsed.defaultCode || '').trim(),
        partners: parsed.partners || {}
      };
    } catch {
      return { defaultCode: '', partners: {} };
    }
  }

  saveLocalState(patch: Partial<CasCommissionLocalState>): void {
    const cur = this.getLocalState();
    const next: CasCommissionLocalState = {
      defaultCode: patch.defaultCode !== undefined ? patch.defaultCode : cur.defaultCode,
      partners: { ...cur.partners, ...(patch.partners || {}) }
    };
    localStorage.setItem(CAS_COMMISSION_STORAGE_KEY, JSON.stringify(next));
  }

  saveDefaultCode(defaultCode: string): void {
    this.saveLocalState({ defaultCode: defaultCode.trim() });
  }

  /**
   * Taux CAS unique utilisé par le tableau comparateur (liste CAS_COMMISSION_LIST).
   * Ne lit pas l’API CAC par partenaire.
   */
  getCasTableCommissionRaw(): string {
    return this.getLocalState().defaultCode || '';
  }

  /**
   * Fusionne réponse API Brokins puis valeurs locales, puis défaut global.
   */
  mergeForPartners(
    remote: Record<string, string>,
    remoteOk: boolean,
    localState: CasCommissionLocalState,
    partnerKeys: string[]
  ): Record<string, string> {
    const out: Record<string, string> = {};
    for (const key of partnerKeys) {
      const lower = key.toLowerCase();
      let v = '';
      if (remoteOk) {
        v = (remote[lower] || remote[key] || '').trim();
      }
      if (!v) {
        v = (
          localState.partners[lower] ||
          localState.partners[key] ||
          localState.partners[key.toUpperCase()] ||
          ''
        ).trim();
      }
      if (!v) {
        v = (localState.defaultCode || '').trim();
      }
      out[lower] = v;
    }
    return out;
  }

  /** GET `/api/comparaison/commission_settings` (même auth que le reste du portail). */
  getRemoteSettings(): Observable<{ settings: Record<string, string>; ok: boolean; httpStatus?: number }> {
    return this.http.get<Record<string, string>>(this.baseUrl, { headers: this.getAuthHeaders() }).pipe(
      timeout(12000),
      map((settings) => ({ settings: settings || {}, ok: true })),
      catchError((err: unknown) => {
        if (err instanceof TimeoutError) {
          return of({ settings: {}, ok: false, httpStatus: 0 });
        }
        if (err instanceof HttpErrorResponse) {
          return of({ settings: {}, ok: false, httpStatus: err.status });
        }
        return of({ settings: {}, ok: false, httpStatus: 0 });
      })
    );
  }

  updateCommission(partner: string, commissionCode: string): Observable<{ ok: boolean; localOnly: boolean }> {
    const code = (commissionCode || '').trim();
    const pKey = partner.trim().toUpperCase();

    return this.http
      .put(`${this.baseUrl}/${encodeURIComponent(partner)}`, { commission_code: code }, { headers: this.getAuthHeaders() })
      .pipe(
        map(() => {
          const state = this.getLocalState();
          this.saveLocalState({
            partners: {
              ...state.partners,
              [pKey]: code,
              [pKey.toLowerCase()]: code
            }
          });
          return { ok: true, localOnly: false };
        }),
        catchError(() => {
          const state = this.getLocalState();
          this.saveLocalState({
            partners: {
              ...state.partners,
              [pKey]: code,
              [pKey.toLowerCase()]: code
            }
          });
          return of({ ok: true, localOnly: true });
        })
      );
  }

  applyDefaultToAllPartners(partnerKeys: string[], defaultCode: string): void {
    const code = defaultCode.trim();
    if (!code) {
      return;
    }
    const state = this.getLocalState();
    const partners = { ...state.partners };
    for (const k of partnerKeys) {
      const up = k.toUpperCase();
      const low = k.toLowerCase();
      partners[up] = code;
      partners[low] = code;
    }
    this.saveLocalState({ defaultCode: code, partners });
  }

  /** Envoie le même code sur l’API pour chaque partenaire (après mise à jour locale). */
  syncDefaultToAllRemote(partnerKeys: string[], defaultCode: string): Observable<{ saved: number; failed: number }> {
    const code = defaultCode.trim();
    if (!code) {
      return of({ saved: 0, failed: 0 });
    }
    this.applyDefaultToAllPartners(partnerKeys, defaultCode);

    const requests = partnerKeys.map((k) =>
      this.http
        .put(`${this.baseUrl}/${encodeURIComponent(k)}`, { commission_code: code }, { headers: this.getAuthHeaders() })
        .pipe(
          map(() => true),
          catchError(() => of(false))
        )
    );

    return forkJoin(requests).pipe(
      map((results) => ({
        saved: results.filter(Boolean).length,
        failed: results.filter((r) => !r).length
      }))
    );
  }
}
